import { Card } from "@/app/admin/_common/ui/card";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { formatUtcDateTime, dayjsTz } from "@/app/_utils/dayjs";
import { getSignupsTimestamps } from "@/app/_utils/guestSignups";
import type { GuestOrderRow } from "@/app/_services/GuestService";
import { Activity } from "lucide-react";

type ActivityEvent = { key: string; timestamp: number; label: string };

// Synthesized from the only two real timestamped sources on a guest — there's
// no timestamped check-in event anywhere in the schema (Order.redeemed is a
// count, not a dated event), so check-in only ever appears as the aggregate
// "check-in rate" in the KPI row, never as a timeline entry here.
function buildActivity(guest: any, orders: GuestOrderRow[]): ActivityEvent[] {
  const signups = getSignupsTimestamps(guest.signups);
  const signupEvents: ActivityEvent[] = signups.map((ts, i) => ({
    key: `signup-${ts}`,
    timestamp: ts,
    label: i === 0 ? "Joined the waitlist" : "Re-signed up for the waitlist",
  }));
  const orderEvents: ActivityEvent[] = orders.map((o) => ({
    key: `order-${o.id}`,
    timestamp: dayjsTz(o.created_at).valueOf(),
    label: `Ordered ${o.quantity} ${o.quantity === 1 ? "ticket" : "tickets"} — ${o.show_title ?? "Show"}`,
  }));
  return [...signupEvents, ...orderEvents].sort((a, b) => b.timestamp - a.timestamp);
}

export default function GuestActivityCard({ guest, orders }: { guest: any; orders: GuestOrderRow[] }) {
  const events = buildActivity(guest, orders).slice(0, 15);

  return (
    <Card className="flex max-h-[320px] flex-col rounded-none border-0 px-2 py-4 shadow-none md:rounded-md md:border md:px-4">
      <div className="mb-5 flex-none text-[16px] font-semibold text-foreground sm:mb-3 sm:text-[13.5px]">Activity</div>
      {events.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <CuoEmpty icon={Activity} text="No activity yet" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pl-1.5">
          <div className="flex flex-col gap-3 border-l border-border pl-3">
            {events.map((event, i) => (
              <div key={event.key} className="relative">
                <span
                  className={`absolute -left-[17px] top-1 size-2.5 rounded-full sm:-left-[16.5px] sm:size-2 ${
                    i === 0 ? "bg-foreground" : "bg-muted-foreground/30"
                  }`}
                />
                <div className="text-[14px] text-foreground sm:text-[12.5px]">{event.label}</div>
                <div className="text-[12px] text-muted-foreground sm:text-[11px]">
                  {formatUtcDateTime(event.timestamp, "MMM D, YYYY h:mm a")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
